import { Hero } from '@/components/homeowners/Hero';
import type { BottomLogosProps } from '@/components/renters/BottomLogos';
import BottomLogos from '@/components/renters/BottomLogos';
import { PageWrapper, Section } from '@/components/shared/Content';
import Layout from '@/components/shared/Layout';
import { SEO } from '@/components/shared/SEO';
import type { Config } from '@/pageConfig/homeowners';
import dynamic from 'next/dynamic';
import React from 'react';
import styled from 'styled-components';

const Closing = dynamic(() => import('@/components/homeowners/Closing').then(mod => mod.Closing));
const Switching = dynamic(() => import('@/components/homeowners/Switching'));
const Trusted = dynamic(() => import('@/components/homeowners/Trusted').then(mod => mod.Trusted));
const Coverage = dynamic(() => import('@/components/homeowners/coverage/Coverage').then(mod => mod.Coverage));
const Faq = dynamic(() => import('@/components/homeowners/faq/Faq').then(mod => mod.Faq));
const Videos = dynamic(() => import('@/components/homeowners/videos/Videos').then(mod => mod.Videos));
const CustomersReviews = dynamic(() => import('@/components/shared').then(mod => mod.CustomersReviews));
const HomeBundle = dynamic(() => import('@/components/homeowners/HomeBundle').then(mod => mod.HomeBundle), {
  ssr: false,
});
const Products = dynamic(() => import('@/components/shared').then(mod => mod.Products));
const BottomCTA = dynamic(() => import('@/components/homeowners/BottomCTA').then(mod => mod.BottomCTA));
const SwitchingEurope = dynamic(() => import('@/components/home/SwitchingEurope').then(mod => mod.SwitchingEurope));

export interface HoBottomLogosProps extends BottomLogosProps {
  darkBackground?: boolean;
  layout?: {
    pt?: number[] | number;
    mt?: number[] | number;
  };
}

export const StyledSection = styled(Section)`
  padding-top: 150px;
  margin-top: -220px;
`;

const HoBottomLogos: React.FC<HoBottomLogosProps> = ({ darkBackground = true, layout, ...restProps }) => {
  return (
    <Section darkBackground={darkBackground} {...layout}>
      <BottomLogos {...restProps} />
    </Section>
  );
};

const componentsByName = {
  hero: Hero,
  customersReviews: CustomersReviews,
  trusted: Trusted,
  closing: Closing,
  switchingEurope: SwitchingEurope,
  videos: Videos,
  coverage: Coverage,
  homeBundle: HomeBundle,
  faq: Faq,
  bottomCTA: BottomCTA,
  bottomLogos: HoBottomLogos,
  products: Products,
  bundleSection: HomeBundle,
  switching: Switching,
} as const;

interface HomeownersPageTemplateProps {
  config: Config;
}

export const HomeownersPageTemplate: React.FC<HomeownersPageTemplateProps> = ({ config }) => {
  return (
    <Layout {...config.layout}>
      <SEO {...config.seo} />
      <PageWrapper>
        {Object.entries(config).map(([componentName, componentProps]) => {
          if (componentName === 'seo' || componentName === 'layout') return null;

          const Component = componentsByName[componentName as keyof typeof componentsByName];

          if (Component == null) {
            console.error(`Component ${componentName} not found`);
            return null;
          }

          return <Component key={componentName} {...componentProps} {...componentProps.layout} />;
        })}
      </PageWrapper>
    </Layout>
  );
};
